import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  InputBase,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Smartphone as PhoneIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { RTL } from "../../../cache/CacheRtl";
import { t } from "./coachTokens";
import { sendPushToTrainees } from "../../../services/AdminMessageService";

const TEMPLATES = [
  { key: "workout",  btn: "💪 תזכורת אימון",  title: "תזכורת לאימון 💪",  body: "היי! יש לך אימון מתוכנן היום. בוא נשבור שיאים! 🔥" },
  { key: "checkin",  btn: "📋 צ׳ק-אין",        title: "זמן לצ׳ק-אין 📋",     body: "שלום! שבוע חדש התחיל. מלא את הצ׳ק-אין השבועי שלך ועדכן אותי בהתקדמות." },
  { key: "renewal",  btn: "🔄 חידוש מנוי",     title: "חידוש מנוי 🔄",       body: "המנוי שלך מסתיים בקרוב. צור קשר לחידוש ולהמשך ההתקדמות שלנו יחד!" },
  { key: "progress", btn: "🎉 כל הכבוד",       title: "כל הכבוד! 🎉",        body: "השגת את היעד שלך השבוע! מרוצה ממך — ממשיכים להתקדם!" },
];

const inputSx = {
  padding: "7px 11px",
  border: `1.5px solid ${t.border}`,
  borderRadius: t.rs,
  background: t.bg,
  color: t.text,
  fontSize: "13px",
  "& input, & textarea": { padding: 0 },
  "&:focus-within": { borderColor: t.green, background: t.white, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" },
};

const SendPushDialog = ({ open, onClose, trainee, onSent }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTick, setSentTick] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setBody("");
      setSending(false);
      setSentTick(false);
    }
  }, [open]);

  const applyTemplate = (k) => {
    const tpl = TEMPLATES.find((x) => x.key === k);
    if (!tpl) return;
    setTitle(tpl.title);
    setBody(tpl.body);
  };

  const send = async () => {
    if (!body.trim() || sending || !trainee?.traineeId) return;
    setSending(true);
    try {
      await sendPushToTrainees([trainee.traineeId], title.trim() || null, body.trim());
      setSentTick(true);
      setTimeout(() => {
        onSent?.();
        onClose?.();
      }, 800);
    } catch (e) {
      onSent?.(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <RTL>
        <Box dir="rtl">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 18px", borderBottom: `1px solid ${t.border}`, background: `linear-gradient(135deg, ${t.greenLight}, #f4fbf8)` }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: t.greenText, display: "flex", alignItems: "center", gap: "6px" }}>
              <SendIcon sx={{ fontSize: 16 }} />
              שליחת הודעת פוש
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: t.t2 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ padding: "16px 18px" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 200px" }, gap: "16px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t2, display: "flex", alignItems: "center", gap: "4px", textTransform: "uppercase", letterSpacing: ".05em" }}>
                    <PersonIcon sx={{ fontSize: 12 }} /> נמען
                  </Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      gap: "3px",
                      background: t.greenLight,
                      color: t.greenText,
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "4px 10px",
                      borderRadius: "20px",
                      border: "1px solid rgba(1,187,167,.2)",
                    }}
                  >
                    {trainee?.traineeName || "—"}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t2, textTransform: "uppercase", letterSpacing: ".05em" }}>כותרת ההודעה</Box>
                  <InputBase
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, 50))}
                    placeholder="למשל: תזכורת לאימון 💪"
                    sx={inputSx}
                  />
                  <Box sx={{ fontSize: 10.5, color: title.length > 45 ? t.red : t.t3, textAlign: "left" }}>{title.length}/50</Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t2, textTransform: "uppercase", letterSpacing: ".05em" }}>גוף ההודעה</Box>
                  <InputBase
                    value={body}
                    onChange={(e) => setBody(e.target.value.slice(0, 120))}
                    placeholder="כתוב הודעה אישית..."
                    multiline
                    minRows={3}
                    sx={{ ...inputSx, height: "auto" }}
                  />
                  <Box sx={{ fontSize: 10.5, color: body.length > 110 ? t.red : t.t3, textAlign: "left" }}>{body.length}/120</Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t2, textTransform: "uppercase", letterSpacing: ".05em" }}>תבניות מהירות</Box>
                  <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                    {TEMPLATES.map((tpl) => (
                      <Box
                        key={tpl.key}
                        component="button"
                        onClick={() => applyTemplate(tpl.key)}
                        sx={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          border: `1px solid ${t.border}`,
                          background: t.bg,
                          color: t.t2,
                          fontSize: 11,
                          fontWeight: 500,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          "&:hover": { background: t.greenLight, borderColor: t.green, color: t.greenText },
                        }}
                      >
                        {tpl.btn}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ background: t.bg, padding: "14px", borderRadius: t.rs, display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t2, display: "flex", alignItems: "center", gap: "4px", marginBottom: "8px" }}>
                  <PhoneIcon sx={{ fontSize: 12 }} /> תצוגה מקדימה
                </Box>
                <Box sx={{ width: 160, background: "#1c1c2e", borderRadius: "24px", padding: "10px 8px 14px", boxShadow: "0 12px 40px rgba(0,0,0,.3)" }}>
                  <Box sx={{ width: 50, height: 6, background: "#2a2a3e", borderRadius: "3px", margin: "0 auto 10px" }} />
                  <Box sx={{ background: "#f5f5f0", borderRadius: "14px", overflow: "hidden", minHeight: 100 }}>
                    <Box sx={{ background: "rgba(255,255,255,.96)", margin: "6px", borderRadius: "12px", padding: "10px", boxShadow: "0 2px 10px rgba(0,0,0,.12)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "5px" }}>
                        <Box sx={{ fontSize: 12 }}>💪</Box>
                        <Box sx={{ fontSize: 10, fontWeight: 700, color: "#333", flex: 1 }}>פרוגרס</Box>
                        <Box sx={{ fontSize: 9, color: "#999" }}>עכשיו</Box>
                      </Box>
                      <Box sx={{ fontSize: 11, fontWeight: 700, color: "#111", marginBottom: "3px", lineHeight: 1.3 }}>
                        {title || "כותרת ההודעה"}
                      </Box>
                      <Box sx={{ fontSize: 10.5, color: "#555", lineHeight: 1.35 }}>
                        {body || "גוף ההודעה יופיע כאן..."}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <Box sx={{ display: "flex", gap: "8px", padding: "12px 18px", borderTop: `1px solid ${t.border}`, background: t.bg, justifyContent: "flex-end" }}>
            <Box
              component="button"
              onClick={onClose}
              sx={{ padding: "8px 14px", borderRadius: t.rs, background: "transparent", color: t.t2, border: `1.5px solid ${t.border}`, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}
            >
              בטל
            </Box>
            <Box
              component="button"
              onClick={send}
              disabled={!body.trim() || sending}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "8px 16px",
                borderRadius: t.rs,
                background: sentTick ? t.greenDark : t.green,
                color: "#fff",
                border: "none",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: !body.trim() || sending ? "not-allowed" : "pointer",
                opacity: !body.trim() ? 0.6 : 1,
                fontFamily: "inherit",
                "&:hover": { background: t.greenDark },
              }}
            >
              {sentTick ? <CheckIcon sx={{ fontSize: 14 }} /> : <SendIcon sx={{ fontSize: 14 }} />}
              {sentTick ? "נשלח!" : "שלח פוש"}
            </Box>
          </Box>
        </Box>
      </RTL>
    </Dialog>
  );
};

export default SendPushDialog;
